import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../services/firebase";
import { useNavigate } from "react-router-dom";
import React, {useEffect, useState} from "react";
import style from "./Login.module.css";
import HeaderAffida from "../../components/header/HeaderAffida";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { FormLabel } from "../../components/form-label/FormLabel";
import {
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormHelperText,
  FormControl,
  InputLabel,
  OutlinedInput, Link, Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {passwordSchema} from "../../services/shared";
import {useAuth} from "../../providers/AuthProvider";

const formSchema = z.object({
  email: z
    .string({ required_error: "Email obbligatoria" })
    .email("Email non valida"),
  password: passwordSchema
});
type FormData = z.infer<typeof formSchema>;

export default function Login() {
  const {grants,loading} = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  // redirect automatico quando grants sono pronti
  useEffect(() => {
    if (loading || grants.length === 0) return;
    if (grants.includes("quality_op")) return void navigate("/lead-list");
    if (grants.includes("create_lead")) return void navigate("/lead-collect");
    if (grants.includes("lp_standalone")) return void navigate("/lp-list");
    navigate("/dashboard");
  }, [grants, loading, navigate]);

  const handleLogin = handleSubmit(async (data) => {
    try {
      setLoginError(null);
      await signInWithEmailAndPassword(auth, data.email, data.password);
      // niente navigate qui — ci pensa l'useEffect quando grants è pronto
    } catch (err) {
      console.error(err);
      setLoginError("Credenziali non valide");
    }
  });
  
  return (
    <div className="container">
      <HeaderAffida />
      <div className={style.loginBox}>
        <FormLabel label="Login"></FormLabel>
        <form onSubmit={handleLogin} className="flex-col gap-2">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Email"
                placeholder="Inserisci email"
                value={field.value ?? ""}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.password}>
                <InputLabel htmlFor="password">Password</InputLabel>
                <OutlinedInput
                  id="password"
                  {...field}
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
                <FormHelperText>{errors.password?.message}</FormHelperText>
              </FormControl>
            )}
          />
          <Link sx={{ color: '#4e3fff' }} variant={"caption"} href={"/reset-password"} style={{transform: "translate(2px, -10px)"}}>Hai dimenticato la password?</Link>
          {loginError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {loginError}
              </Alert>
          )}
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Elaboro richiesta..." : "Entra"}
          </Button>
        </form>
      </div>
    </div>
  );
}
